import * as React from 'react';

import classNames from 'classnames';

import { isUserRightToLeft } from 'js/lib/language';

import { breakPoint, color } from '@coursera/coursera-ui';
import { SvgChevronLeft, SvgChevronRight } from '@coursera/coursera-ui/svg';

import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';
import SocialProofItem from 'bundles/front-page/components/modules/FallbackSocialProofItem';
import { getSocialProofs } from 'bundles/front-page/constants/defaults';
import type { SocialProofItemProp } from 'bundles/front-page/types';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/front-page';

const slidesToShowDefault = 3;
const slidesToShowXl = 3;
const slidesToShowLg = 2;
const slidesToShowMd = 1;
const slidesToShowSm = 1;
const slidesAvailable = getSocialProofs().length;

const ArrowButton = ({ isArrowRight, ...rest }: { isArrowRight?: boolean }) => {
  const classes = classNames('slider-button', {
    'slider-left': !isArrowRight,
    'slider-right': isArrowRight,
  });
  const isRTL = isUserRightToLeft();
  const nextSlideTitle = _t('Next slide');
  const prevSlideTitle = _t('Previous slide');

  return (
    <div className={classes}>
      <TrackedButton trackingName={isArrowRight ? 'social_proof_arrow_right' : 'social_proof_arrow_left'} {...rest}>
        {isArrowRight ? (
          <SvgChevronRight
            title={isRTL ? prevSlideTitle : nextSlideTitle}
            size={48}
            color="#0056D2"
            hoverColor={color.primaryText}
          />
        ) : (
          <SvgChevronLeft
            title={isRTL ? nextSlideTitle : prevSlideTitle}
            size={48}
            color="#0056D2"
            hoverColor={color.primaryText}
          />
        )}
      </TrackedButton>
    </div>
  );
};

const CAROUSEL_SETTINGS: CarouselSettings = {
  infinite: true,
  nextArrow: <ArrowButton isArrowRight />,
  prevArrow: <ArrowButton />,
  speed: 300,
  slidesToShow: slidesToShowDefault,
  slidesToScroll:
    slidesAvailable % slidesToShowDefault === 0 ? slidesToShowDefault : slidesAvailable % slidesToShowDefault,
  dots: false,
  initialSlide: 0,
  lazyLoad: 'ondemand',
  responsive: [
    {
      breakpoint: breakPoint.xl,
      settings: {
        slidesToShow: slidesToShowXl,
        slidesToScroll: slidesAvailable % slidesToShowXl === 0 ? slidesToShowXl : slidesAvailable % slidesToShowXl,
      },
    },
    {
      breakpoint: breakPoint.lg,
      settings: {
        slidesToShow: slidesToShowLg,
        slidesToScroll: slidesAvailable % slidesToShowLg === 0 ? slidesToShowLg : slidesAvailable % slidesToShowLg,
      },
    },
    {
      breakpoint: breakPoint.md,
      settings: {
        slidesToShow: slidesToShowMd,
        slidesToScroll: slidesAvailable % slidesToShowMd === 0 ? slidesToShowMd : slidesAvailable % slidesToShowMd,
      },
    },
    {
      breakpoint: breakPoint.sm,
      settings: {
        slidesToShow: slidesToShowSm,
        slidesToScroll: slidesAvailable % slidesToShowSm === 0 ? slidesToShowSm : slidesAvailable % slidesToShowSm,
      },
    },
  ],
};

const renderSocialProofItems = (socialProofItems: Array<SocialProofItemProp>) => {
  return socialProofItems.map((proof) => {
    const { name, title, subtitle, redesignBody, rebrandImageUrl } = proof;
    return (
      <SocialProofItem
        key={name}
        name={name}
        title={title}
        subtitle={subtitle}
        body={redesignBody}
        rebrandImageUrl={rebrandImageUrl}
      />
    );
  });
};

type Props = {
  socialProofItems: Array<SocialProofItemProp>;
};

const TestimonialCarousel: React.FC<Props> = ({ socialProofItems }) => {
  return <Carousel settings={CAROUSEL_SETTINGS}>{renderSocialProofItems(socialProofItems)}</Carousel>;
};

export default TestimonialCarousel;
